import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { apiClient } from "@/common/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

interface UserAssessment {
  answer: string | null;
  score: number | null;
  answeredAt: string | null;
}

interface Review {
  comment: string | null;
  score?: number | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewedByName: string | null;
}

interface Response {
  questionId: string;
  questionText: string;
  questionListId: string;
  order: number;
  userAssessment: UserAssessment;
  managementReview: Review;
  directorReview: Review;
  hrReview: Review;
}

interface UserKPIForm {
  _id: string;
  kpiFormTitle: string;
  status: string;
  userName: string;
  userEmail: string;
  responses: Response[];
  reviewStatus: string | null;
  reviewFinalizedAt: string | null;
  reviewFinalizedByName: string | null;
}

interface QuestionListGroup {
  questionListId: string;
  questions: Response[];
}

export const FormReviewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState<UserKPIForm | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (id) {
      fetchForm();
    }
  }, [id]);

  const fetchForm = async () => {
    try {
      const response = await apiClient.get(`/api/user-kpi-form/${id}`);
      if (response.success) {
        setForm(response.data);
      } else {
        toast.error("Failed to load form");
      }
    } catch (error) {
      toast.error("Failed to load form");
    } finally {
      setIsLoading(false);
    }
  };

  const groupByQuestionList = (responses: Response[]): QuestionListGroup[] => {
    const grouped = responses.reduce((acc, response) => {
      const listId = response.questionListId;
      if (!acc[listId]) {
        acc[listId] = [];
      }
      acc[listId].push(response);
      return acc;
    }, {} as Record<string, Response[]>);

    return Object.entries(grouped).map(([questionListId, questions]) => ({
      questionListId,
      questions: questions.sort((a, b) => a.order - b.order),
    }));
  };

  const handleReviewChange = (
    questionId: string,
    reviewType: "managementReview" | "directorReview" | "hrReview",
    value: string
  ) => {
    if (!form || !user) return;

    const updatedResponses = form.responses.map((r) =>
      r.questionId === questionId
        ? {
            ...r,
            [reviewType]: {
              ...r[reviewType],
              comment: value,
              reviewedAt: new Date().toISOString(),
              reviewedBy: user.id,
              reviewedByName: user.name,
            },
          }
        : r
    );

    setForm({ ...form, responses: updatedResponses });
  };

  const handleManagementScoreChange = (questionId: string, value: string) => {
    if (!form || !user) return;

    const updatedResponses = form.responses.map((r) =>
      r.questionId === questionId
        ? {
            ...r,
            managementReview: {
              ...r.managementReview,
              score: parseInt(value),
              reviewedAt: new Date().toISOString(),
              reviewedBy: user.id,
              reviewedByName: user.name,
            },
          }
        : r
    );

    setForm({ ...form, responses: updatedResponses });
  };

  const handleSaveReviews = async () => {
    if (!form) return;

    setIsSaving(true);
    try {
      const response = await apiClient.put(`/api/user-kpi-form/${id}`, {
        responses: form.responses,
      });

      if (response.success) {
        toast.success("Reviews saved successfully");
        fetchForm();
      } else {
        toast.error("Failed to save reviews");
      }
    } catch (error) {
      toast.error("Failed to save reviews");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinalizeReview = async () => {
    if (!form) return;

    setIsFinalizing(true);
    try {
      const response = await apiClient.post(
        `/api/user-kpi-form/${id}/finalize-review`
      );

      if (response.success) {
        toast.success("Review finalized successfully");
        fetchForm();
      } else {
        toast.error("Failed to finalize review");
      }
    } catch (error) {
      toast.error("Failed to finalize review");
    } finally {
      setIsFinalizing(false);
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!form) {
    return <div className="p-4">Form not found</div>;
  }

  const questionListGroups = groupByQuestionList(form.responses);
  const isReviewCompleted = form.reviewStatus === "completed";

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {form.kpiFormTitle}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Review KPI Form - {form.userName} ({form.userEmail})
              </p>
              {isReviewCompleted && (
                <div className="mt-2">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Review Completed
                  </span>
                  {form.reviewFinalizedByName && (
                    <span className="ml-2 text-xs text-gray-500">
                      by {form.reviewFinalizedByName}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSaveReviews}
                disabled={isSaving || isReviewCompleted}
                variant="outline"
              >
                {isSaving ? "Saving..." : "Save Reviews"}
              </Button>
              <Button
                onClick={handleFinalizeReview}
                disabled={isFinalizing || isReviewCompleted}
              >
                {isFinalizing ? "Finalizing..." : "Finalize Review"}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          {questionListGroups.map((group, index) => (
            <Button
              key={group.questionListId}
              variant={currentStep === index ? "default" : "outline"}
              size="sm"
              onClick={() => setCurrentStep(index)}
            >
              {index + 1}
            </Button>
          ))}
        </div>

        <div className="bg-white rounded-lg border shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Section {currentStep + 1}</h2>
          </div>

          <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
            {questionListGroups[currentStep].questions.map(
              (question, qIndex) => (
                <div
                  key={question.questionId}
                  className="rounded-lg p-5 border space-y-4"
                >
                  <div>
                    <h3 className="font-semibold text-gray-900 text-base leading-relaxed">
                      {qIndex + 1}. {question.questionText}
                    </h3>
                  </div>

                  <div className="bg-gray-50 rounded p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700">
                      User Assessment
                    </h4>
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-600 block">
                        Answer
                      </label>
                      <div className="text-sm text-gray-900">
                        {question.userAssessment.answer || "No answer provided"}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-600 block">
                        Self Score
                      </label>
                      <div className="text-sm text-gray-900">
                        {question.userAssessment.score || "No score"}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 block">
                      Management Comment
                    </label>
                    <Textarea
                      value={question.managementReview.comment || ""}
                      onChange={(e) =>
                        handleReviewChange(
                          question.questionId,
                          "managementReview",
                          e.target.value
                        )
                      }
                      placeholder="Add management comment..."
                      className="bg-white"
                      rows={3}
                      disabled={isReviewCompleted}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 block">
                      Management Score (1-5)
                    </label>
                    <div className="flex gap-1.5 flex-wrap">
                      {[
                        { score: 1, label: "Unacceptable" },
                        { score: 2, label: "Need Improvement" },
                        { score: 3, label: "Meet Expectations" },
                        { score: 4, label: "Exceed Expectations" },
                        { score: 5, label: "Outstanding" },
                      ].map(({ score, label }) => (
                        <Button
                          key={score}
                          type="button"
                          variant={
                            question.managementReview.score === score
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          className="flex-col h-auto py-1.5 px-2.5 text-xs"
                          onClick={() =>
                            handleManagementScoreChange(
                              question.questionId,
                              score.toString()
                            )
                          }
                          disabled={isReviewCompleted}
                        >
                          <div className="font-semibold text-sm">{score}</div>
                          <div className="text-[10px] font-normal leading-tight">
                            {label}
                          </div>
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 block">
                      Director Comment
                    </label>
                    <Textarea
                      value={question.directorReview.comment || ""}
                      onChange={(e) =>
                        handleReviewChange(
                          question.questionId,
                          "directorReview",
                          e.target.value
                        )
                      }
                      placeholder="Add director comment..."
                      className="bg-white"
                      rows={3}
                      disabled={isReviewCompleted}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 block">
                      HR Comment
                    </label>
                    <Textarea
                      value={question.hrReview.comment || ""}
                      onChange={(e) =>
                        handleReviewChange(
                          question.questionId,
                          "hrReview",
                          e.target.value
                        )
                      }
                      placeholder="Add HR comment..."
                      className="bg-white"
                      rows={3}
                      disabled={isReviewCompleted}
                    />
                  </div>
                </div>
              )
            )}
          </div>

          <div className="flex justify-between mt-6 pt-6 border-t">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
            >
              Previous
            </Button>
            <Button
              onClick={() =>
                setCurrentStep(
                  Math.min(questionListGroups.length - 1, currentStep + 1)
                )
              }
              disabled={currentStep === questionListGroups.length - 1}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
